import { ConnectComponentsProvider, ConnectNotificationBanner } from '@stripe/react-connect-js';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { LinkButton } from '../../components/ui/Button';
import { api } from '../../lib/api';
import { useDocumentTitle } from '../../lib/title';
import { useConnectInstance, useStripeStatus } from './connect';

/**
 * Everything about being paid, in one place with its own addresses.
 *
 * ## Why these are separate pages
 *
 * These accounts are created with `stripe_dashboard[type] = none`, so a host has
 * no Stripe dashboard to look at - the platform is the only place they can see
 * what they have been paid and when the next one lands. Stripe asks for those
 * surfaces by URL before it will let a platform take accounts live, one row per
 * page, so each one is a route rather than a tab that leaves the address alone.
 *
 * It is also the better shape regardless. Getting paid used to be two cards at
 * the bottom of the host application form, which is where somebody goes once.
 *
 * ## One Connect instance for all of them
 *
 * Built here and provided downward. Each page would otherwise build its own,
 * and two instances race over the same account - see `connect.ts`.
 *
 * ## The banner is on every page, not one of them
 *
 * It is how Stripe reaches a host with an outstanding requirement or a failed
 * verification, and there is no dashboard for them to find it in. Putting it
 * only on the index would mean somebody who bookmarked their payouts never sees
 * the notice saying why those payouts stopped.
 */
export function EarningsLayout() {
  useDocumentTitle('Earnings');

  const queryClient = useQueryClient();
  const { data, isPending, enabled, isHost } = useStripeStatus();
  const connect = useConnectInstance(data?.connected);
  const [starting, setStarting] = useState(false);
  const [failed, setFailed] = useState(false);

  /*
   * Two different reasons to see nothing, and they are not the same sentence.
   * Telling a host to apply for hosting because a flag is off is the kind of
   * message that generates a support conversation about the wrong thing.
   */
  if (!enabled || !isHost) {
    return (
      <Shell>
        <Card className="flex flex-col items-start gap-3 p-6" data-earnings-unavailable>
          <p className="text-sm text-soft">
            {!enabled
              ? 'Payouts are not switched on yet. Nothing to see here until they are.'
              : 'Earnings are for hosts. Apply to host and this is where the money side lives.'}
          </p>
          {!enabled ? null : <LinkButton to="/host">About hosting</LinkButton>}
        </Card>
      </Shell>
    );
  }

  if (isPending) {
    return (
      <Shell>
        <p className="font-mono text-[10px] text-ghost">loading…</p>
      </Shell>
    );
  }

  /*
   * Before there is an account there is nothing to show and nothing to link to,
   * so this page is the one button rather than an empty set of tabs.
   */
  if (!data?.connected) {
    const start = async () => {
      setStarting(true);
      setFailed(false);

      try {
        await api.post('/me/stripe/account');
        await queryClient.invalidateQueries({ queryKey: ['stripe', 'status'] });
      } catch {
        setFailed(true);
      } finally {
        setStarting(false);
      }
    };

    return (
      <Shell>
        <Card className="p-6" data-payouts-panel>
          <h2 className="mb-1 text-sm font-medium uppercase tracking-wider text-faint">Payouts</h2>

          <p className="mb-4 text-sm text-soft">
            Connect a Stripe account to receive tips and paid boosts. Stripe handles the identity
            checks and pays you directly, and we never hold your money.
          </p>

          {failed && (
            <div className="mb-3">
              <Alert tone="error">That did not start. Try again in a moment.</Alert>
            </div>
          )}

          <Button onClick={start} loading={starting} data-connect-stripe>
            Set up payouts
          </Button>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell>
      {connect ? (
        <ConnectComponentsProvider connectInstance={connect}>
          <div data-connect-banner>
            <ConnectNotificationBanner />
          </div>

          <Tabs ready={data.payouts_enabled} />

          <Outlet />
        </ConnectComponentsProvider>
      ) : (
        /*
         * No publishable key in this build, which is a build-time variable -
         * Vite inlines `import.meta.env` at compile time, so a bundle built
         * without it cannot be fixed by setting it on the server.
         *
         * Said out loud rather than degraded quietly. Stripe's components
         * render nothing at all outside a provider - no error, no warning - so
         * without this the page is headings over empty cards, which looks like
         * an account with no payouts rather than a deployment with no key.
         * Our own statement still works, so the page keeps it.
         */
        <>
          <Alert tone="error">
            Payout details are unavailable. This build has no Stripe key. Your earnings below
            are unaffected.
          </Alert>

          <Tabs ready={data.payouts_enabled} />
          <Outlet />
        </>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5 px-6 py-10" data-earnings>
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Earnings</h1>
        <p className="mt-1 text-sm text-soft">What you have been paid, and where it goes.</p>
      </div>

      {children}
    </div>
  );
}

/**
 * Payouts and balances are hidden until Stripe says the account can be paid.
 *
 * Both components render an error rather than an empty state on an account that
 * has not finished onboarding, and sending somebody to an error to find out
 * they have a form to fill in is a worse answer than the form.
 */
function Tabs({ ready }: { ready: boolean }) {
  const tabs = [
    ['/earnings', 'Summary', true],
    ['/earnings/payouts', 'Payouts', ready],
    ['/earnings/balances', 'Balances', ready],
  ] as const;

  return (
    <nav className="flex gap-1 border-b border-line" data-earnings-tabs>
      {tabs
        .filter(([, , show]) => show)
        .map(([to, label]) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/earnings'}
            data-earnings-tab={label.toLowerCase()}
            className={({ isActive }) =>
              `meta-caps -mb-px border-b-2 px-3 py-2 transition-colors ${
                isActive
                  ? 'border-accent text-ink'
                  : 'border-transparent text-faint hover:text-soft'
              }`
            }
          >
            {label}
          </NavLink>
        ))}
    </nav>
  );
}
